import { useMemo } from 'react';

import {
  CauseBackerFamily,
  PropertyValue,
  useShowConsumerDirectoryBacker,
} from '@givebacks/api/backer';
import { useUserData } from '@givebacks/api/core';
import { useFormyValidator } from '@givebacks/formy';
import { Center, InfoListCard, Loader, Stack, Title } from 'ui';
import { UseFormReturnType } from 'ui/Form';

import { phoneNumberDisplayFormat, toTitleCase } from '../../../../../../utils';
import { CauseDirectorySettingsFormType } from './CauseDirectorySettingsForm';

const PropertiesPreview = ({ properties }: { properties?: PropertyValue[] }) => {
  return properties?.map((property) => {
    if (!property.property || !property.value) return null;
    return (
      <InfoListCard.Item
        key={property.uuid}
        label={`${property.property.name}:`}
        detail={property.value}
      />
    );
  });
};

const FamilyPreview = ({ families }: { families?: CauseBackerFamily[] }) => {
  const adults = useMemo(() => {
    if (!families) return [];
    const allAdults = families
      .flatMap((family) => family.family_members || [])
      .map((member) => `${member.backer.first_name} ${member.backer.last_name}`);

    // Remove duplicates by name for adults in multiple families
    return Array.from(new Set(allAdults));
  }, [families]);

  const children = useMemo(() => {
    if (!families) return [];
    const allChildren = families
      .flatMap((family) => family.children || [])
      .map((child) => `${child.first_name} ${child.last_name}`);

    // Remove duplicates by name for children in multiple families
    return Array.from(new Set(allChildren));
  }, [families]);

  if (!adults.length && !children.length) return null;

  return (
    <>
      {adults.length > 0 && <InfoListCard.Item label="Adults:" detail={adults.join(', ')} />}
      {children.length > 0 && <InfoListCard.Item label="Children:" detail={children.join(', ')} />}
    </>
  );
};

const CauseDirectoryPreview = ({ causeId }: { causeId: string }) => {
  const { data: user } = useUserData();
  const { data: directoryBacker, isPending: isLoadingDirectoryBacker } =
    useShowConsumerDirectoryBacker({
      causeId,
      backerId: user?.uuid,
    });

  const displayName = useMemo(() => {
    if (!user?.first_name && !user?.last_name) return null;
    return toTitleCase(`${user.first_name} ${user.last_name}`, ' ').trim();
  }, [user?.first_name, user?.last_name]);

  const { useGetForm } = useFormyValidator<{
    directorySettings: UseFormReturnType<CauseDirectorySettingsFormType>;
  }>();
  const form = useGetForm('directorySettings');

  if (!form?.values?.inDirectory) return null;
  if (isLoadingDirectoryBacker)
    return (
      <Center>
        <Loader />
      </Center>
    );
  return (
    <Stack>
      <Title order={3} fz="lg">
        Directory Preview
      </Title>
      <InfoListCard color="gray" direction="column" cols={1}>
        {displayName && <InfoListCard.Item label="Name:" detail={displayName} />}
        {form?.values?.showEmail && (
          /**
           * User email is always available, whereas the directory backer email might not be returned if previous settings to show email were false
           */
          <InfoListCard.Item label="Email:" detail={user?.email} />
        )}

        {form?.values?.showPhone && (
          /**
           * User phone number is always available, whereas the directory backer phone number might not be returned if previous settings to show phone were false
           */
          <InfoListCard.Item
            label="Phone:"
            detail={phoneNumberDisplayFormat(user?.phone_number ?? '') || '(###) ###-####'}
          />
        )}
        <FamilyPreview families={directoryBacker?.families} />
        <PropertiesPreview properties={directoryBacker?.values} />
      </InfoListCard>
    </Stack>
  );
};

export { CauseDirectoryPreview };
